<script lang="ts">
  import { formatBytes, imageSource, PREVIEW_MAX } from '../logic/preview';

  interface Props {
    /** Side name shown in the caption ("BASE", "HEAD"). */
    label: string;
    /** Media type of the image (SVG too: always shown as an <img> from `imageSource`, never inlined). */
    mime: string;
    /** Loads the bytes; null when the file has no version on this side. */
    load: (() => Promise<Uint8Array | null>) | null;
    /** Shown when there is no version on this side. */
    empty?: string;
  }
  let { label, mime, load, empty = 'No file on this side' }: Props = $props();

  type Loaded = { status: 'loading' } | { status: 'missing' } | { status: 'error'; error: string } | { status: 'ready'; bytes: Uint8Array };
  let loaded = $state<Loaded>({ status: 'loading' });
  let force = $state(false);
  let dims = $state<{ w: number; h: number } | null>(null);
  let broken = $state(false);
  let url = $state<string | null>(null);

  $effect(() => {
    const l = load;
    let cancelled = false;
    loaded = { status: 'loading' };
    dims = null;
    broken = false;
    force = false;
    if (!l) {
      loaded = { status: 'missing' };
      return;
    }
    l().then(
      (bytes) => {
        if (!cancelled) loaded = bytes ? { status: 'ready', bytes } : { status: 'missing' };
      },
      (e) => {
        if (!cancelled) loaded = { status: 'error', error: e instanceof Error ? e.message : String(e) };
      },
    );
    return () => {
      cancelled = true;
    };
  });

  const bytes = $derived(loaded.status === 'ready' ? loaded.bytes : null);
  const large = $derived(!!bytes && bytes.length > PREVIEW_MAX);

  $effect(() => {
    if (!bytes || (large && !force)) {
      url = null;
      return;
    }
    const src = imageSource(bytes, mime);
    url = src.url;
    return src.release;
  });
</script>

<figure class="img-side">
  <figcaption>
    <span class="side-name">{label}</span>
    {#if dims}<span>{dims.w.toLocaleString()} × {dims.h.toLocaleString()} px</span>{/if}
    {#if bytes}<span>{formatBytes(bytes.length)}</span>{/if}
  </figcaption>
  <div class="img-stage">
    {#if loaded.status === 'loading'}
      <span class="preview-message">Loading…</span>
    {:else if loaded.status === 'missing'}
      <span class="preview-message">{empty}</span>
    {:else if loaded.status === 'error'}
      <span class="preview-message">Could not load the image: {loaded.error}</span>
    {:else if large && !force}
      <span class="preview-message"
        >This image is {formatBytes(bytes!.length)}, over the {formatBytes(PREVIEW_MAX)} shown by default.
        <button class="micro" onclick={() => (force = true)}>Show anyway</button></span
      >
    {:else if broken}
      <span class="preview-message">This file could not be decoded as {mime}.</span>
    {:else if url}
      <img
        src={url}
        alt="{label} version"
        onload={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          dims = { w: img.naturalWidth, h: img.naturalHeight };
        }}
        onerror={() => (broken = true)}
      />
    {/if}
  </div>
</figure>
